import logoUrl from '../assets/logo.png';

/**
 * layout.js — Shared Navigation & Footer Injector
 * Dynamically injects the navbar and footer into subpages.
 * Home (index.html) has its own inline nav/footer, so this
 * is used only by services.html, about.html, process.html, contact.html.
 */

/**
 * Determines the correct path prefix based on current page depth.
 * All pages are at root level, so paths are relative to root.
 */
function getBasePath() {
  return './';
}

/**
 * Returns the current page filename for active-nav highlighting.
 */
function getCurrentPage() {
  const path = window.location.pathname;
  const file = path.split('/').pop() || 'index.html';
  return file;
}

/**
 * Injects the shared navigation bar.
 */
export function injectNav() {
  const navTarget = document.getElementById('shared-nav');
  if (!navTarget) return;

  const currentPage = getCurrentPage();
  const base = getBasePath();

  const isActive = (page) => currentPage === page ? 'active' : '';

  navTarget.innerHTML = `
    <nav>
      <a href="${base}index.html" class="nav-logo">
        <img src="${logoUrl}" alt="Razaek Technologies" onerror="this.style.display='none';this.nextElementSibling.style.display='block'">
        <span style="display:none;font-family:var(--font);font-weight:800;font-size:20px;background:var(--grad);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;">Razaek</span>
      </a>
      <ul class="nav-links">
        <li><a href="${base}services.html" class="${isActive('services.html')}">Services</a></li>
        <li><a href="${base}about.html" class="${isActive('about.html')}">Why Us</a></li>
        <li><a href="${base}process.html" class="${isActive('process.html')}">Process</a></li>
        <li><a href="${base}contact.html" class="nav-cta ${isActive('contact.html')}">Get Free Quote</a></li>
      </ul>
      <button class="nav-hamburger" aria-label="Toggle menu">
        <span></span><span></span><span></span>
      </button>
    </nav>
  `;

  // Mobile menu toggle
  const hamburger = navTarget.querySelector('.nav-hamburger');
  const navLinks = navTarget.querySelector('.nav-links');
  if (hamburger && navLinks) {
    hamburger.addEventListener('click', () => {
      navLinks.classList.toggle('nav-open');
      hamburger.classList.toggle('nav-active');
    });

    // Close menu when a link is clicked
    navLinks.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        navLinks.classList.remove('nav-open');
        hamburger.classList.remove('nav-active');
      });
    });
  }
}

/**
 * Injects the shared footer.
 */
export function injectFooter() {
  const footerTarget = document.getElementById('shared-footer');
  if (!footerTarget) return;

  const base = getBasePath();

  footerTarget.innerHTML = `
    <footer>
      <div class="footer-top">
        <div>
          <div class="footer-logo">
            <img src="${logoUrl}" alt="Razaek Technologies" onerror="this.style.display='none'">
          </div>
          <p class="footer-desc">Your digital growth partner for web & app development, serving clients globally from India.</p>
        </div>
        <div class="footer-col">
          <h5>Services</h5>
          <ul>
            <li><a href="${base}services.html">Logo & Branding</a></li>
            <li><a href="${base}services.html">UI/UX Design</a></li>
            <li><a href="${base}services.html">Web Development</a></li>
            <li><a href="${base}services.html">App Development</a></li>
            <li><a href="${base}services.html">Digital Marketing</a></li>
          </ul>
        </div>
        <div class="footer-col">
          <h5>Company</h5>
          <ul>
            <li><a href="${base}about.html">About Us</a></li>
            <li><a href="${base}process.html">Our Process</a></li>
            <li><a href="${base}contact.html">Contact</a></li>
          </ul>
        </div>
        <div class="footer-col">
          <h5>Contact</h5>
          <ul>
            <li><a href="https://wa.me/995593571032" target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
            <li><a href="mailto:hello@razaektechnologies.com">Email Us</a></li>
          </ul>
        </div>
      </div>
      <div class="footer-bottom">
        <p>© ${new Date().getFullYear()} Razaek Technologies. All rights reserved.</p>
        <div class="footer-socials">
          <a href="https://www.facebook.com/razaektechnologies" target="_blank" rel="noopener noreferrer" title="Facebook">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
          </a>
          <a href="https://www.instagram.com/razaektechnologies" target="_blank" rel="noopener noreferrer" title="Instagram">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
          </a>
          <a href="https://x.com/razaek_tech" target="_blank" rel="noopener noreferrer" title="X (Twitter)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
          <a href="https://www.linkedin.com/company/razaek-technologies" target="_blank" rel="noopener noreferrer" title="LinkedIn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
          </a>
          <a href="https://www.youtube.com/@razaektechnologies" target="_blank" rel="noopener noreferrer" title="YouTube">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
          </a>
          <a href="https://wa.me/995593571032" target="_blank" rel="noopener noreferrer" title="WhatsApp">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
          </a>
        </div>
      </div>
    </footer>
  `;
}
